"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";

import { Card, CardContent } from "./ui/card";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { useSendFlowState } from "../hooks/useSendFlowState";
import { useAvailableAssets } from "../hooks/query/queries";
import { useFeatureAccess } from "./FeatureGate";
import Send from "./Send";
import Receive from "./Receive";
import SendConfirmation from "./SendConfirmation";
import {
  sendCrypto,
  verify2FA,
  createSendQuote,
  refreshPortfolioAndAssets,
} from "../services/digitalWalletService";
import { getAllChains } from "../services/tradingDataService";
import SendComplete from "./SendSuccess";
import AuthTwoFA from "./AuthTwoFA";
import { SecuredFooter } from "./ui/secured-footer";
import TwoFA from "./MainTwoFA";
import AddContact from "./AddContact";
import { useQueryClient } from "@tanstack/react-query";
import { refreshBalanceQueries } from "../services/invalidator";
import CardSpinningLoader from "./CardSpinningLoader";

const SendReceivePage = ({
  assetToSelect,
  tab,
  tradingAssets,
  tradingNets,
  childTab,
}) => {
  const { loadState, saveState, clearState, isHydrated } = useSendFlowState();
  const { flags: featureFlags, isLoading: isLoadingFlags } = useFeatureAccess(
    "mainFeatures",
    ["sendCrypto", "receiveCrypto"]
  );
  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [amount, setAmount] = useState(null);
  const [zarAmount, setZarAmount] = useState(null);
  const [destinationAddress, setDestinationAddress] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [currentRate, setCurrentRate] = useState(null);
  const [tagNumber, setTagNumber] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [hasBeenSetUp, setHasBeenSetUp] = useState(false);
  const [amountFlag, setAmountFlag] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [isTwoFAOpen, setIsTwoFAOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [pendingNewAddressFlow, setPendingNewAddressFlow] = useState(null);
  const [triggerQuoteAfterAddContact, setTriggerQuoteAfterAddContact] =
    useState(false);
  const [creatingQuoteAfterAddContact, setCreatingQuoteAfterAddContact] =
    useState(false);
  const [justEnabled2FAInThisFlow, setJustEnabled2FAInThisFlow] =
    useState(false);

  const { toast } = useToast();
  const { userProfile, settings, updateSettings } = useAuth();
  const queryClient = useQueryClient();

  const [networks, setNetworks] = useState([]);

  const balanceParams = {
    id: userProfile?._id,
    type: "Crypto",
  };

  const { data: availableAssets } = useAvailableAssets({
    isSend: true,
    isEightyeightPay: false,
    balanceParams,
    balanceParamsEightEight: { id: userProfile?._id },
  });

  const assetsToUse = tradingAssets || availableAssets;

  useEffect(() => {
    if (!assetsToUse || assetsToUse.length === 0) return;

    const fetchNetworks = async () => {
      try {
        const allChains = await getAllChains();

        const assetIds = assetsToUse.map((asset) => asset.assetId || asset._id);
        const filteredNetworks = allChains.filter((chain) =>
          assetIds.includes(chain.assetId)
        );

        setNetworks(filteredNetworks);
      } catch (error) {}
    };

    fetchNetworks();
  }, [assetsToUse]);

  useEffect(() => {
    if (!isHydrated) return;

    const savedState = loadState();

    if (savedState) {
      setSelectedAsset(savedState.selectedAsset || null);
      setAmount(savedState.amount || null);
      setZarAmount(savedState.zarAmount || null);
      setDestinationAddress(savedState.destinationAddress || "");
      setSelectedNetwork(savedState.selectedNetwork || []);
      setTagNumber(savedState.tagNumber || null);
      setQuoteData(savedState.quoteData || null);

      if (savedState.currentStep === "confirmation") {
        setShowConfirmation(true);
      } else if (savedState.currentStep === "auth") {
        setShowAuth(true);
      } else if (savedState.currentStep === "success") {
        setShowSuccess(true);
      }
    }
  }, [isHydrated, loadState]);

  useEffect(() => {
    if (settings) {
      const isEnabled = settings?.security?.twoFactorAuth?.enabled;
      setIsEnabled(isEnabled);
    }
  }, [settings]);

  useEffect(() => {
    if (settings) {
      const localCurrency = settings?.preferences?.localCurrency || "ZAR";
      setCurrency(localCurrency);
    }
    if (!settings) {
      const localCurrency = "ZAR";
      setCurrency(localCurrency);
    }
  }, [settings]);

  useEffect(() => {
    if (isLoadingFlags) return;
    if (selectedTab !== null) return;
    if (featureFlags?.sendCrypto) {
      setSelectedTab("send");
    } else if (featureFlags?.receiveCrypto) {
      setSelectedTab("receive");
    }
  }, [featureFlags, selectedTab, isLoadingFlags]);

  useEffect(() => {
    const validTabs = ["send", "receive"];
    if (!tab) return;
    if (validTabs.includes(tab)) {
      setSelectedTab(tab);
    }
  }, [tab]);
  useEffect(() => {
    const validTabs = ["send", "receive"];
    if (!childTab) return;
    if (validTabs.includes(childTab)) {
      setSelectedTab(childTab);
    }
  }, [childTab]);

  const handleTabChange = (value) => {
    setSelectedTab(value);
  };

  const handleSelectedAsset = useCallback((asset, rate) => {
    // Validate asset before processing
    if (!asset || !asset?.assetId || !asset?.asset) {
      return;
    }

    // Use functional updates to avoid dependency on current state values
    setSelectedAsset((prevAsset) => {
      if (prevAsset?.assetId === asset?.assetId) {
        return prevAsset; // No change, return same reference
      }
      return asset;
    });

    if (
      rate !== null &&
      rate !== undefined &&
      typeof rate === "number" &&
      rate > 0
    ) {
      setCurrentRate((prevRate) => {
        const prevRateValue =
          prevRate?.rate || (typeof prevRate === "number" ? prevRate : null);
        if (prevRateValue === rate) {
          return prevRate; // No change, return same reference
        }
        return { rate };
      });
    }
  }, []);
  const handleQuote = async (cryptoAmount = null) => {
    try {
      setIsCreatingQuote(true);
      setIsSending(true);

      let finalCryptoAmount = null;
      if (
        cryptoAmount !== null &&
        cryptoAmount !== undefined &&
        !isNaN(cryptoAmount) &&
        cryptoAmount > 0
      ) {
        finalCryptoAmount = cryptoAmount;
      } else if (
        amount &&
        !isNaN(parseFloat(amount)) &&
        parseFloat(amount) > 0
      ) {
        finalCryptoAmount = parseFloat(amount);
      } else if (zarAmount && currentRate?.rate) {
        finalCryptoAmount = zarAmount / currentRate.rate;
      }

      if (
        !finalCryptoAmount ||
        finalCryptoAmount <= 0 ||
        isNaN(finalCryptoAmount)
      ) {
        toast({
          description: "Please enter a valid amount",
          variant: "eighty",
        });
        setIsSending(false);
        setIsCreatingQuote(false);
        return;
      }

      const quotePayload = {
        amount: finalCryptoAmount,
        cryptoAsset: selectedAsset?.asset,
        chain: selectedNetwork?._id,
      };

      const result = await createSendQuote(quotePayload);
      const resData = result.data.data;

      if (result) {
        const totalWithFee = resData?.totalAmount;
        if (selectedAsset?.availableAmount < totalWithFee) {
          setAmountFlag(true);
          setIsSending(false);
        }

        if (selectedAsset?.availableAmount >= totalWithFee) {
          setAmountFlag(false);
          handleContinue(resData);
        }
      }
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
      setIsSending(false);
      setCreatingQuoteAfterAddContact(false);
      setSelectedTab("send");
    } finally {
      setIsCreatingQuote(false);
    }
  };
  const handleChangeAmount = useCallback((amount, zar) => {
    setAmount(amount);
    setZarAmount(zar);
  }, []);

  const SendDataArray = {
    walletAddress:
      typeof destinationAddress === "object"
        ? destinationAddress?.walletAddress
        : destinationAddress,
    transactionType: "send",
    amount: quoteData?.amountReceived,
    cryptoAsset: selectedAsset?.asset,
    chain: selectedNetwork?._id,
    fee: quoteData?.withdrawalFee ?? "0",
    userId: userProfile?._id,
    tagNumber: tagNumber ? tagNumber : "",
    // Travel Rule Fields
    beneficiaryFirstName:
      typeof destinationAddress === "object"
        ? destinationAddress?.firstName
        : undefined,
    beneficiaryLastName:
      typeof destinationAddress === "object"
        ? destinationAddress?.lastName
        : undefined,
    beneficiaryCategory:
      typeof destinationAddress === "object"
        ? destinationAddress?.category
        : undefined,
    walletType:
      typeof destinationAddress === "object"
        ? destinationAddress?.walletType
        : undefined,
    provider:
      typeof destinationAddress === "object"
        ? destinationAddress?.provider
        : undefined,
  };

  const hasValidSendInputs = useCallback(() => {
    const address =
      typeof destinationAddress === "object"
        ? destinationAddress?.walletAddress
        : destinationAddress;
    const hasAddress =
      typeof address === "string" ? address?.trim()?.length > 0 : !!address;
    const hasAmount =
      amount != null && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0;
    const hasAsset = !!selectedAsset?.assetId;
    const hasNetwork = !!selectedNetwork?._id;
    const hasQuote = !!quoteData?.amountReceived;
    return hasAddress && hasAmount && hasAsset && hasNetwork && hasQuote;
  }, [destinationAddress, amount, selectedAsset, selectedNetwork, quoteData]);

  const resetToInitialSendScreen = useCallback(() => {
    setShowConfirmation(false);
    setShowAuth(false);
    setSelectedTab("send");
    setHasBeenSetUp(false);
    setJustEnabled2FAInThisFlow(false);
    clearState();
  }, [clearState]);

  const handleConfirm = async () => {
    try {
      setIsConfirming(true);
      const result = await sendCrypto(SendDataArray);
      if (result) {
        setShowAuth(false);
        setShowConfirmation(false);
        setShowSuccess(true);
        // Refresh portfolio data first, then invalidate/refetch dependent queries
        await refreshPortfolioAndAssets();
        await refreshBalanceQueries(queryClient);
        saveState({
          currentStep: "success",
          selectedAsset,
          amount,
          zarAmount,
          destinationAddress,
          selectedNetwork,
          tagNumber,
          quoteData,
        });
      }
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
      setShowAuth(true);
      setShowConfirmation(true);
      setShowSuccess(false);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleContinue = (quoteData) => {
    setCreatingQuoteAfterAddContact(false);
    setQuoteData(quoteData);
    setShowConfirmation(true);
    setSelectedTab("send");
    saveState({
      currentStep: "confirmation",
      selectedAsset,
      amount,
      zarAmount,
      destinationAddress,
      selectedNetwork,
      tagNumber,
      quoteData,
    });
  };

  const handleQuoteRef = useRef(handleQuote);
  handleQuoteRef.current = handleQuote;
  useEffect(() => {
    if (!triggerQuoteAfterAddContact || !destinationAddress) return;
    setTriggerQuoteAfterAddContact(false);
    handleQuoteRef.current();
  }, [triggerQuoteAfterAddContact, destinationAddress]);

  const twoFactor = () => {
    if (hasBeenSetUp) {
      if (!hasValidSendInputs()) {
        resetToInitialSendScreen();
        return;
      }
      handleConfirm();
    }
    if (!hasBeenSetUp) {
      setShowConfirmation(false);
      setShowAuth(true);
      saveState({
        currentStep: "auth",
        selectedAsset,
        amount,
        zarAmount,
        destinationAddress,
        selectedNetwork,
        tagNumber,
        quoteData,
      });
    }
  };

  const handleVerify = async (token) => {
    try {
      const twoFAdata = {
        token: token,
      };
      const verifiedTwoFA = await verify2FA(twoFAdata);
      if (verifiedTwoFA) {
        updateSettings(verifiedTwoFA);
        toast({
          description: `2FA ${isEnabled ? "verified" : "enabled"} Successfully`,
          variant: "eighty",
        });
        if (!hasValidSendInputs()) {
          resetToInitialSendScreen();
          return;
        }
        handleConfirm();
      }
    } catch (error) {
      toast({
        description: `${error.message}`,
        variant: "eighty",
      });
      setIsVerifying(false);
    } finally {
      setIsVerifying(false);
    }
  };
  const handleSelectedChain = useCallback((chain) => {
    setSelectedNetwork(chain);
  }, []);
  const handleSetup = () => {
    setHasBeenSetUp(true);
  };
  const handleBackfromTwoFA = () => {
    setShowAuth(false);
    setShowConfirmation(true);
  };
  const handleCancel = () => {
    setShowConfirmation(false);
    setIsSending(false);
    setSelectedTab("send");
    setSelectedAsset(null);
    setAmount(null);
    setZarAmount(null);
    setShowAuth(false);
    clearState();
  };
  return (
    <>
      {!showConfirmation && !showSuccess && !showAuth && (
        <Card className="w-full lg:pt-8 pt-5 !shadow-none bg-white rounded-2xl">
          <div className="lg:px-8 px-5">
            {selectedTab !== "addContact" && (
              <Tabs onValueChange={handleTabChange} className="mb-8">
                <TabsList
                  className={`grid rounded-full bg-transparent border border-[#EBE9EF] border-[1px] h-[64px] px-[8px] ${featureFlags?.sendCrypto && featureFlags?.receiveCrypto ? "grid-cols-2" : "grid-cols-1"}`}
                >
                  {featureFlags?.sendCrypto && (
                    <TabsTrigger
                      value="send"
                      className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                        selectedTab === "send"
                          ? "bg-[#33125F] text-white"
                          : "text-[#6D6584] bg-transparent"
                      }`}
                    >
                      Send
                    </TabsTrigger>
                  )}
                  {featureFlags?.receiveCrypto && (
                    <TabsTrigger
                      value="receive"
                      className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                        selectedTab === "receive"
                          ? "bg-[#33125F] text-white"
                          : "text-[#6D6584] bg-transparent"
                      }`}
                    >
                      Receive
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            )}

            {selectedTab === "send" && !showConfirmation ? (
              <>
                <Send
                  hotActionAsset={tab === "send" ? assetToSelect : null}
                  onChangeAmount={handleChangeAmount}
                  onSelectAsset={handleSelectedAsset}
                  onSelectChain={handleSelectedChain}
                  selectedAsset={selectedAsset}
                  setSelectedAsset={setSelectedAsset}
                  setTagNumber={setTagNumber}
                  destinationAddress={destinationAddress}
                  setDestinationAddress={setDestinationAddress}
                  onContinue={handleContinue}
                  loadingOnContinue={isCreatingQuote}
                  onhandleQuote={handleQuote}
                  setAmountFlag={setAmountFlag}
                  amountFlag={amountFlag}
                  parentAmount={amount}
                  setAmount={setAmount}
                  isSending={isSending || isCreatingQuote}
                  setIsSending={setIsSending}
                  currentRate={currentRate}
                  selectedNetwork={selectedNetwork}
                  zarAmount={zarAmount}
                  twoFactor={twoFactor}
                  tradingAssets={assetsToUse}
                  networks={networks}
                  onOpenAddContact={() => {
                    setSelectedTab("addContact");
                  }}
                  onAddressNotInContacts={(payload) => {
                    setPendingNewAddressFlow(payload);
                    setSelectedTab("addContact");
                  }}
                />
              </>
            ) : selectedTab === "receive" ? (
              <Receive
                hotActionAsset={tab === "receive" ? assetToSelect : null}
                selectedAsset={selectedAsset}
                setSelectedAsset={setSelectedAsset}
                tradingNets={
                  (tradingNets?.length > 0 ? tradingNets : networks) || []
                }
                tradingAssets={
                  (tradingAssets?.length > 0 ? tradingAssets : assetsToUse) ||
                  []
                }
              />
            ) : selectedTab === "addContact" ? (
              creatingQuoteAfterAddContact ? (
                <CardContent className="flex flex-col items-center justify-center h-[600px]">
                  <CardSpinningLoader logoTopposition="top-[88%]" />
                </CardContent>
              ) : (
                <AddContact
                  hotActionAsset={assetToSelect}
                  selectedAsset={selectedAsset}
                  setSelectedAsset={setSelectedAsset}
                  selectedNetwork={selectedNetwork}
                  setSelectedNetwork={setSelectedNetwork}
                  initialAddress={pendingNewAddressFlow?.address ?? undefined}
                  onContactAdded={(newWallet) => {
                    if (newWallet) {
                      setDestinationAddress(newWallet);
                      if (newWallet.asset && assetsToUse) {
                        const assetMatch = assetsToUse.find(
                          (a) => a.assetId === newWallet.asset
                        );
                        if (assetMatch) {
                          setSelectedAsset(assetMatch);
                        }
                      }
                      if (newWallet.chain && networks) {
                        const chainMatch = networks.find(
                          (net) => net._id === newWallet.chain
                        );
                        if (chainMatch) {
                          setSelectedNetwork(chainMatch);
                        }
                      }
                      if (pendingNewAddressFlow) {
                        setPendingNewAddressFlow(null);
                        setCreatingQuoteAfterAddContact(true);
                        setTriggerQuoteAfterAddContact(true);
                        return;
                      }
                    }
                    setPendingNewAddressFlow(null);
                    setSelectedTab("send");
                  }}
                />
              )
            ) : null}
          </div>
          <SecuredFooter />
        </Card>
      )}

      {showConfirmation && !showSuccess && (
        <>
          <SendConfirmation
            quoteData={quoteData}
            asset={selectedAsset}
            zar={zarAmount}
            amount={amount}
            currency={currency}
            currentRate={currentRate}
            destinationAddress={destinationAddress}
            onCancel={handleCancel}
            onConfirm={twoFactor}
            setUp={handleSetup}
            isConfirming={isConfirming}
          />
        </>
      )}
      {showAuth === true &&
        !showSuccess &&
        !showConfirmation &&
        (isEnabled && !justEnabled2FAInThisFlow ? (
          <AuthTwoFA
            onVerify={handleVerify}
            onBack={handleBackfromTwoFA}
            isVerifying={isVerifying}
            setIsVerifying={setIsVerifying}
          />
        ) : (
          <TwoFA
            isOpen={isTwoFAOpen}
            closeOverlay={() => {
              setHasBeenSetUp(false);
              setJustEnabled2FAInThisFlow(false);
              setIsTwoFAOpen(false);
              setShowAuth(false);
            }}
            isSetUp={handleSetup}
            on2FAEnabled={() => setJustEnabled2FAInThisFlow(true)}
            onDoneClick={() => {
              setHasBeenSetUp(false);
              setJustEnabled2FAInThisFlow(false);
              setIsTwoFAOpen(false);
              setShowConfirmation(false);
              setShowAuth(false);
            }}
          />
        ))}
      {showSuccess === true && !showConfirmation && !showAuth && (
        <SendComplete
          assetName={selectedAsset?.asset || "ETH"}
          onDone={() => {
            setShowSuccess(false);
            setDestinationAddress(null);
            clearState();
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default SendReceivePage;
